import { Seo } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { Reveal, SectionHead } from "../components/site/Motion";
import { IconCard } from "../components/site/IconCard";
import { ProcessSteps } from "../components/site/ProcessSteps";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { CTAButton } from "../components/site/CTAButton";
import { IMG, PARTNER_TYPES, PARTNER_FLOW } from "../content/site";
import { MilestoneStat } from "../components/site/MilestoneStat";
import { Eyebrow, MaskLines } from "../components/site/Motion";

const EXPERIENCE_POINTS = [
  ["Industry experience", "Hands-on involvement in solar projects from development through to commissioning gives us a practical view of what partners need from a consumer opportunity."],
  ["Consumer relationships", "We work directly with C&I decision-makers, so requirements, sites and commercial expectations are understood before partners are introduced."],
  ["Facilitated connections", "We bring consumers and implementation partners to the table with a clear brief, and coordinate the conversation through proposals, agreements and execution."],
];

const ExperienceBand = () => (
  <section className="relative bg-[#082619] py-24 md:py-32 grain" data-testid="partner-experience">
    <div className="relative z-10 mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Eyebrow tone="light">Why partner with us</Eyebrow></Reveal>
        <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.05] tracking-tight text-[#F9F8F3]">
          <MaskLines inView lines={["Experience that", <span className="font-serif-i text-[#74DC26]">connects</span>, "demand with delivery."]} />
        </h2>
        <Reveal delay={0.2} className="mt-10"><MilestoneStat /></Reveal>
      </div>
      <div className="lg:col-span-7">
        {EXPERIENCE_POINTS.map(([t, d], i) => (
          <Reveal key={t} delay={i * 0.08} className="grid gap-3 border-b border-white/10 py-8 md:grid-cols-[1fr_1.5fr]">
            <h3 className="font-display text-2xl font-medium text-[#F9F8F3]">{t}</h3>
            <p className="text-base leading-relaxed text-[#B4C7BA]">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LOOKING_FOR = [
  "Experience delivering C&I rooftop or open access projects",
  "Clarity on geographies, capacity ranges and commercial models you offer",
  "Transparent proposals that can be compared on a like-for-like basis",
  "Commitment to quality execution and long-term operations",
];

export default function PartnerPage() {
  return (
    <>
      <Seo title="Partner With Us" path="/partner" description="Solar developers, EPC companies and renewable energy investors: collaborate with Solitaire India on qualified commercial and industrial solar opportunities across India." />
      <PageHero
        eyebrow="Partner with us"
        title={["Build what's next", <>with <span className="font-serif-i text-[#3f8a0f]">qualified</span> C&I</>, "opportunities."]}
        intro="For solar developers, EPC companies and renewable energy investors. Backed by industry experience across nearly 500 MWp of projects helped to commission, we facilitate well-prepared connections between C&I consumers and implementation partners."
        image={IMG.park}
        imageAlt="Expansive utility-scale solar park"
      >
        <div className="mt-8"><CTAButton href="#partner-form" variant="forest" testId="partner-hero-cta">Start a Conversation</CTAButton></div>
      </PageHero>
      <ExperienceBand />
      <section className="bg-[#F9F8F3] py-24 md:py-32" data-testid="partner-types">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <SectionHead eyebrow="Who we work with" title={["Partners across the", "solar value chain."]} />
          <div className="mt-16 grid gap-4 md:grid-cols-3">
            {PARTNER_TYPES.map((p, i) => <IconCard key={p.id} {...p} i={i} prefix="partner-type" />)}
          </div>
        </div>
      </section>
      <section className="bg-[#F2EFE7] py-24 md:py-32" data-testid="partner-flow">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <SectionHead eyebrow="How collaboration works" title={["A structured way", "to engage."]} />
          <div className="mt-16"><ProcessSteps steps={PARTNER_FLOW} /></div>
        </div>
      </section>
      <section id="partner-form" className="scroll-mt-24 bg-[#F9F8F3] py-24 md:py-32" data-testid="partner-form-section">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead eyebrow="What we look for" title={["Let's explore", "a partnership."]} />
            <ul className="mt-10 space-y-4">
              {LOOKING_FOR.map((l, i) => (
                <Reveal key={l} delay={i * 0.06} as="li" className="flex gap-4 border-b border-[#E5E1D3] pb-4 text-[#082619]">
                  <span className="font-mono text-sm text-[#3f8a0f]">0{i + 1}</span>{l}
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal className="lg:col-span-7 rounded-2xl border border-[#E5E1D3] bg-white p-6 md:p-12">
            <EnquiryForm defaultType="partner" />
          </Reveal>
        </div>
      </section>
    </>
  );
}
